import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Button,
  Pagination,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import pandoraAdminService from '../../../services/pandoraAdminService';
import type { PandoraDocument, DocumentListResponse } from '../../../services/pandoraAdminService';

const CATEGORY_LABELS: Record<string, string> = {
  petition: 'Petição',
  law: 'Legislação',
  legal_case: 'Jurisprudência',
  legal_theory: 'Doutrina',
  verbetes: 'Verbetes',
};

function getCategoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? category;
}

export default function PandoraValidationPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<DocumentListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const loadQueue = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await pandoraAdminService.getValidationQueue(page, pageSize);
      setData(result);
    } catch (err: any) {
      setError(
        err?.response?.data?.detail || err?.message || 'Erro ao carregar fila de validação.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQueue();
  }, [page]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const totalPages = data ? Math.ceil(data.total / data.page_size) : 0;

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Fila de Validação
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Documentos com status "segmented" aguardando validação.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {data && data.items.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              Nenhum documento na fila de validação no momento.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button variant="outlined" onClick={() => navigate('/admin/pandora')}>
                Voltar ao Painel
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {data?.items.map((doc: PandoraDocument) => (
              <Card key={doc.id}>
                <CardActionArea onClick={() => navigate(`/admin/pandora/documents/${doc.id}`)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h6">{doc.title}</Typography>
                      <Chip label={getCategoryLabel(doc.category)} size="small" variant="outlined" />
                      {doc.legal_domain && (
                        <Chip label={doc.legal_domain} size="small" variant="outlined" />
                      )}
                    </Box>
                    {doc.paragraph_count != null && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {doc.paragraph_count} parágrafo(s)
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      Criado em: {doc.created_at ? new Date(doc.created_at).toLocaleString() : '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Clique para ver detalhes e validar parágrafos.
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
